import React, { useEffect, useState, useMemo, lazy, Suspense } from 'react';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Subtitle from 'components/UI/Subtitle';
import Button from 'components/UI/Button';
import Loader from 'components/UI/Loader';
import OfficePopup from 'components/Popups/OfficePopup';
import OfficesMap from './OfficesMap/OfficesMap';
import SideMenu from './SideMenu/SideMenu';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { useOffices } from './hooks/officesHook';
import { useStateModal } from 'utils/hooks';
import { useLang, useTrans, isRequestedFrom } from 'utils/localisation';
import { pushCustomEvent } from 'utils/analytics';
import { usaRegion, cisRegion, azerRegion, arabicRegion } from 'define/locale';
import * as styles from './AboutUs.module.scss';

const RequestConsultation = lazy(() => import('components/Modals/RequestConsultation'));

function AboutUs({
  title,
  description,
  defaultRegion,
  mapOptions,
  formId = 'about-us-block',
  formName = 'about-us-block',
}) {
  const {
    loading,
    officesList,
    isSmallScreenVersion,
    selectedOffice,
    setSelectedOffice,
    closeOfficePopupHandler,
    coordinates,
    setCoordinates,
    inViewRef,
  } = useOffices();
  const lang = useLang();
  const t = useTrans(['common']);
  const { modalState, showModal, hideModal } = useStateModal();

  const initialRegion = useMemo(() => {
    if (defaultRegion) {
      return defaultRegion;
    }
    if (lang === 'de' || lang === 'pl') {
      return 'all';
    }
    if (lang === 'az') {
      return 'az';
    }
    if (lang === 'ae') {
      return 'ae';
    }
    if (lang === 'kz') {
      return 'kz';
    }
    return null;
  }, [lang, defaultRegion]);

  const [region, setRegion] = useState(initialRegion);

  useEffect(() => {
    if (
      defaultRegion ||
      lang === 'de' ||
      lang === 'az' ||
      lang === 'ae' ||
      lang === 'pl' ||
      lang === 'kz' ||
      (isRequestedFrom(cisRegion) && !isRequestedFrom('kz'))
    ) {
      return;
    }
    if (isRequestedFrom('KZ')) {
      setRegion('kz');
      return;
    }
    if (isRequestedFrom(usaRegion)) {
      setRegion('us');
      return;
    }
    if (isRequestedFrom(azerRegion)) {
      setRegion('az');
      return;
    }
    if (isRequestedFrom(arabicRegion)) {
      setRegion('mena');
      return;
    }
    if (isRequestedFrom(usaRegion)) {
      setRegion('us');
      return;
    }
    setRegion('all');
  }, [lang, defaultRegion]);

  const openModal = () => {
    showModal();
    pushCustomEvent('ndrsn_about_us_block', {
      action: 'click_on_button',
    });
  };

  if (!region) {
    return null;
  }

  return (
    <Container id="offices" background="transparent" className={styles.container} ref={inViewRef}>
      {!isSmallScreenVersion && (
        <div className={styles.mapSection}>
          {loading ? (
            <Loader theme="fullHeight" />
          ) : (
            <>
              <OfficesMap
                coordinates={coordinates}
                setCoordinates={setCoordinates}
                selectedOffice={selectedOffice}
                setSelectedOffice={setSelectedOffice}
                offices={officesList}
                {...mapOptions[region]}
              />
              {selectedOffice && (
                <OfficePopup
                  closeHandler={closeOfficePopupHandler}
                  title={`${selectedOffice.office?.city}, ${selectedOffice.office?.country}`}
                  address={selectedOffice.office?.address}
                  phone={selectedOffice.office?.phone}
                  images={selectedOffice.office?.images}
                  className={styles.officePopup}
                />
              )}
            </>
          )}
        </div>
      )}
      <div className={styles.wrapper}>
        <div className={styles.sideMenuSection}>
          <Title color="white" size="h3" tag="h2">
            {title[region]}
          </Title>
          <Subtitle size="md" color="white">
            {description[region]}
          </Subtitle>
          <Button type="submit" className={styles.button} fullWidthMobile onClick={openModal}>
            {t('requestConsultation')}
          </Button>
          <Suspense fallback={null}>
            <RequestConsultation
              isOpen={modalState.isOpen}
              closeModalCallback={hideModal}
              id={formId}
              formName={formName}
            />
          </Suspense>
        </div>
        {isSmallScreenVersion && (
          <div className={styles.mobileWrapper}>
            {loading ? <Loader theme="block" /> : <SideMenu offices={officesList} />}
          </div>
        )}
      </div>
    </Container>
  );
}

AboutUs.displayName = 'AboutUs';

export default withErrorBoundary(AboutUs);
